import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { eventSearch } from "../services/guest";
import { filterExpiredEvents } from "../utils/eventUtils";

const usePaginatedEvents = (query, city, category, pageSize = 10) => {
  const { t } = useTranslation();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [totalLoaded, setTotalLoaded] = useState(0);

  // Reset pagination when filters change
  useEffect(() => {
    setEvents([]);
    setCurrentPage(1);
    setHasNextPage(true);
    setTotalLoaded(0);
    setError(null);
  }, [query, city, category]);

  const fetchEvents = useCallback(async (page = 1, isLoadMore = false) => {
    try {
      if (isLoadMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      setError(null);

      const response = await eventSearch(query, city, category, page, pageSize);
      
      // Filter out expired events
      const filteredResponse = filterExpiredEvents(response || []);
      
      if (page === 1) {
        setEvents(filteredResponse);
      } else {
        setEvents(prevEvents => [...prevEvents, ...filteredResponse]);
      }
      
      // Determine if there are more pages
      // If we received fewer items than pageSize, we've reached the end
      setHasNextPage((response || []).length === pageSize);
      setTotalLoaded(prev => page === 1 ? filteredResponse.length : prev + filteredResponse.length);
      
    } catch (err) {
      const errorMessage = t("ErrorFetchingEvents") || "Error fetching events";
      setError({ message: errorMessage, originalError: err });
      console.error("Error fetching events:", err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [query, city, category, pageSize, t]);

  // Load initial data
  useEffect(() => {
    fetchEvents(1, false);
  }, [fetchEvents]);

  const loadMore = useCallback(() => {
    if (!loadingMore && hasNextPage) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      fetchEvents(nextPage, true);
    }
  }, [currentPage, loadingMore, hasNextPage, fetchEvents]);

  const refresh = useCallback(() => {
    setCurrentPage(1);
    fetchEvents(1, false);
  }, [fetchEvents]);

  return {
    events,
    loading,
    loadingMore,
    error,
    hasNextPage,
    totalLoaded,
    loadMore,
    refresh
  };
};

export default usePaginatedEvents;